import styled from "@emotion/styled";
import type { FC } from "react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import {
  CM_IN_FOOT,
  cmToInch,
  FOOT_IN_INCHES,
  inchesToCm
} from "src/lib/unitConversion";
import useQuestionnaireCubitInContext from "src/state/QuestionnaireCubit/useQuestionnaireCubitInContext";
import type { QuestionnaireField } from "src/state/QuestionnaireStepCubit/QuestionnaireStepCubit";
import translate from "src/lib/translate";
import { Input } from "@9amhealth/shared";
import { FormErrorContext } from "../QuestionnaireStep/FormErrorContext";

export function calculateNewHeightFromInchesChange(
  currentFeet: number,
  newInches: number
): { feet: number; inches: number } {
  const totalInches = currentFeet * FOOT_IN_INCHES + newInches;
  const feet = Math.floor(totalInches / FOOT_IN_INCHES);
  const inches = totalInches % FOOT_IN_INCHES;

  return {
    feet,
    inches
  };
}

export function parseNumberInput(
  value: string,
  defaultValue: number = 0
): number {
  const parsed = Number.parseInt(value);
  return isNaN(parsed) ? defaultValue : parsed;
}

const Grid = styled.div`
  display: grid;
  grid-template-columns: 7fr 5fr;
  gap: 1em;
`;

const HiddenFieldset = styled.fieldset`
  border: none;
  margin: 0;
  padding: 0;
`;

const HiddenSpan = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
`;

const ErrorMessage = styled.div`
  padding: 0.5em 1em 1em;
  display: block;
  color: var(--primary-warning-red, #bf1902);
  font-size: 0.875rem;

  @keyframes show {
    from {
      opacity: 0;
      transform: translateY(-0.5rem);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  animation: show 0.2s linear forwards;
`;

const HeightInput: FC<{
  field: QuestionnaireField;
}> = (props) => {
  const name = props.field.id;
  const { getValues, setValue } = useFormContext();
  const cm = Number(getValues(name) ?? "");
  const [, { saveValue }] = useQuestionnaireCubitInContext();
  const { setFieldError, clearFieldError } = React.useContext(FormErrorContext);
  const [error, setError] = useState<string | undefined>(undefined);

  const inInches = useMemo(() => cmToInch(cm), [cm]);
  const ft = useRef(Math.floor(inInches / FOOT_IN_INCHES));
  const inches = useRef(Math.round(inInches % FOOT_IN_INCHES));

  const updateFormState = () => {
    setError(undefined);
    clearFieldError(name);
    const convertedToCM = inchesToCm(
      ft.current * FOOT_IN_INCHES + inches.current
    );

    // only save if the value is a valid number and greater than 1 foot
    if (convertedToCM >= CM_IN_FOOT && !isNaN(convertedToCM)) {
      saveValue(name, convertedToCM);
      setValue(name, convertedToCM);

      if (convertedToCM < 50 || convertedToCM > 300) {
        const errorMsg = translate("error_validate_height_invalid");
        setError(errorMsg);
        setFieldError(name, errorMsg);
      }
    } else {
      saveValue(name, undefined);
      setValue(name, undefined);
      clearFieldError(name);
    }
  };

  const handleFeetChange = (newFtString: string) => {
    setError(undefined);
    ft.current = parseNumberInput(newFtString);
    updateFormState();
  };

  const handleInchesChange = (newInchesString: string) => {
    setError(undefined);
    inches.current = parseNumberInput(newInchesString);
    updateFormState();
  };

  useEffect(() => {
    updateFormState();
  }, [inInches]);

  const initialValues = {
    ft: ft.current ? `${ft.current}` : "",
    inch: inches.current ? `${inches.current}` : ""
  };

  return (
    <div>
      <HiddenFieldset aria-describedby="height-help">
        <HiddenSpan id="height-help">
          {translate("enterHeightInFeetInches")}
        </HiddenSpan>
        <Grid>
          <Input
            name="feet"
            defaultValue={initialValues.ft}
            placeholder={translate("feet")}
            label={translate("feet")}
            isRequired
            maxLength={1}
            pattern="[0-9]*"
            mask="0"
            inputMode="numeric"
            aria-label={translate("feet")}
            icon={translate("units.feet")}
            onChange={(e) => handleFeetChange(e)}
          />
          <Input
            name="inches"
            defaultValue={initialValues.inch}
            placeholder={translate("inches")}
            label={translate("inches")}
            maxLength={2}
            mask="0[0]"
            pattern="[0-9]*"
            inputMode="numeric"
            aria-label={translate("inches")}
            icon={translate("units.inches")}
            onChange={(e) => handleInchesChange(e)}
          />
        </Grid>
        {error && <ErrorMessage className="little1">{error}</ErrorMessage>}
      </HiddenFieldset>
    </div>
  );
};

export default HeightInput;
